/**
 * RequisitesWidget Component
 *
 * Виджет для управления реквизитами и договорами организации.
 * Включает список реквизитов, договоров и функционал добавления/редактирования/деактивации.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Stack,
  Button,
  Box,
  Badge,
  ActionIcon,
  Menu,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconFileText,
  IconPlus,
  IconDots,
  IconPencil,
  IconTrash,
  IconStar,
  IconStarFilled,
  IconBuildingBank,
  IconUser,
  IconX,
  IconCheck,
  IconCalendar,
  IconFileDescription,
  IconDownload,
  IconUpload,
} from '@tabler/icons-react';
import BaseWidget from './BaseWidget';
import RequisitesFormModal from './RequisitesFormModal';
import RequisitesViewModal from './RequisitesViewModal';
import RequisitesDeactivateModal from './RequisitesDeactivateModal';
import RequisitesAddChoiceModal from './RequisitesAddChoiceModal';
import RequisitesLinkSection from './RequisitesLinkSection';
import ContractFormModal from './ContractFormModal';
import ContractViewModal from './ContractViewModal';
import ContractDeactivateModal from './ContractDeactivateModal';
import tokens from '../../../theme/designTokens';

/**
 * API функции для реквизитов
 */
const requisitesApi = {
  async getRequisites(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить реквизиты');
    }
    return response.json();
  },

  async createRequisites(organizationId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать реквизиты');
    }
    return response.json();
  },

  async updateRequisites(organizationId, requisitesId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites/${requisitesId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось обновить реквизиты');
    }
    return response.json();
  },

  async deleteRequisites(organizationId, requisitesId) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites/${requisitesId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить реквизиты');
    }
  },

  async deactivateRequisites(organizationId, requisitesId, reason) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites/${requisitesId}/deactivate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось деактивировать реквизиты');
    }
    return response.json();
  },

  async restoreRequisites(organizationId, requisitesId, reason) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites/${requisitesId}/restore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reason }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось восстановить реквизиты');
    }
    return response.json();
  },

  async setPrimary(organizationId, requisitesId) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites/${requisitesId}/set-primary`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось установить основные реквизиты');
    }
    return response.json();
  },

  // Ссылки на заполнение реквизитов
  async getLinks(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites-links`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить ссылки');
    }
    return response.json();
  },

  async createLink(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites-links`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать ссылку');
    }
    return response.json();
  },

  async regenerateLink(organizationId, linkId) {
    const response = await fetch(`/api/organizations/${organizationId}/requisites-links/${linkId}/regenerate`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось перегенерировать ссылку');
    }
    return response.json();
  },
};

/**
 * API функции для договоров
 */
const contractsApi = {
  async getContracts(organizationId, requisitesId = null) {
    let url = `/api/organizations/${organizationId}/contracts`;
    if (requisitesId) {
      url += `?requisites_id=${requisitesId}`;
    }
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Не удалось загрузить договоры');
    }
    return response.json();
  },

  async createContract(organizationId, requisitesId, data, file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('contract_data', JSON.stringify(data));

    const response = await fetch(
      `/api/organizations/${organizationId}/requisites/${requisitesId}/contracts`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать договор');
    }
    return response.json();
  },

  async updateContract(organizationId, contractId, data) {
    const response = await fetch(
      `/api/organizations/${organizationId}/contracts/${contractId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось обновить договор');
    }
    return response.json();
  },

  async deleteContract(organizationId, contractId) {
    const response = await fetch(
      `/api/organizations/${organizationId}/contracts/${contractId}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить договор');
    }
  },

  async uploadFile(organizationId, contractId, file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `/api/organizations/${organizationId}/contracts/${contractId}/files`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось загрузить файл');
    }
    return response.json();
  },

  async deactivateContract(organizationId, contractId, reason) {
    const response = await fetch(
      `/api/organizations/${organizationId}/contracts/${contractId}/deactivate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason }),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось деактивировать договор');
    }
    return response.json();
  },

  async restoreContract(organizationId, contractId, reason) {
    const response = await fetch(
      `/api/organizations/${organizationId}/contracts/${contractId}/restore`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason }),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось восстановить договор');
    }
    return response.json();
  },

  async changeStatus(organizationId, contractId, status) {
    const response = await fetch(
      `/api/organizations/${organizationId}/contracts/${contractId}/status`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      }
    );
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось изменить статус');
    }
    return response.json();
  },
};

/**
 * API для предметов
 */
const subjectsApi = {
  async getSubjects() {
    const response = await fetch('/api/subjects', { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Не удалось загрузить предметы');
    }
    return response.json();
  },
};

// Цвета статусов договоров
const CONTRACT_STATUS_COLORS = {
  draft: 'gray',
  pending_signature: 'yellow',
  signed: 'blue',
  active: 'green',
  paused: 'orange',
  terminated: 'red',
};

/**
 * Компонент отображения договора
 */
const ContractItem = ({
  contract,
  onEdit,
  onDelete,
  onView,
  onDeactivate,
  onRestore,
  onDownload,
  onUploadFile,
}) => {
  const isInactive = !contract.is_active;
  const statusColor = CONTRACT_STATUS_COLORS[contract.status] || 'gray';

  // Форматирование даты
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <Group
      p="sm"
      pl="xl"
      style={{
        borderRadius: '12px',
        backgroundColor: isInactive ? tokens.colors.neutral[2] : tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
        opacity: isInactive ? 0.7 : 1,
        marginLeft: '24px',
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = tokens.colors.neutral[2]}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isInactive ? tokens.colors.neutral[2] : tokens.colors.neutral[1]}
      onClick={() => onView(contract)}
    >
      <Box
        style={{
          width: 32,
          height: 32,
          borderRadius: '8px',
          backgroundColor: isInactive ? '#ffebee' : '#e8f5e9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconFileDescription size={16} color={isInactive ? '#c62828' : '#2e7d32'} />
      </Box>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {contract.display_name}
          </Text>
          <Badge
            size="xs"
            variant={contract.status === 'active' ? 'filled' : 'light'}
            radius="xl"
            color={statusColor}
          >
            {contract.status_display}
          </Badge>
          {isInactive && (
            <Badge
              size="xs"
              variant="filled"
              radius="xl"
              color="red"
              leftSection={<IconX size={10} />}
            >
              Деактивирован
            </Badge>
          )}
          {contract.edit_count > 0 && !isInactive && (
            <Badge
              size="xs"
              variant="light"
              radius="xl"
              color="blue"
            >
              Изменено {contract.edit_count}
            </Badge>
          )}
        </Group>

        <Group gap="xs" mt={2}>
          <Group gap={4}>
            <IconCalendar size={12} color={tokens.colors.neutral[6]} />
            <Text size="xs" c="dimmed">
              {formatDate(contract.signing_date)} — {formatDate(contract.start_date)}
            </Text>
          </Group>
        </Group>

        {contract.subjects && contract.subjects.length > 0 && (
          <Group gap={4} mt={4}>
            {contract.subjects.slice(0, 3).map((subject) => (
              <Badge
                key={subject.id}
                size="xs"
                variant="light"
                radius="xl"
                color="blue"
              >
                {subject.name}
              </Badge>
            ))}
            {contract.subjects.length > 3 && (
              <Text size="xs" c="dimmed">
                +{contract.subjects.length - 3}
              </Text>
            )}
          </Group>
        )}
      </Box>

      {/* Меню действий */}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          <Menu.Item
            leftSection={<IconDownload size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onDownload(contract);
            }}
          >
            Скачать файл
          </Menu.Item>

          <Menu.Item
            leftSection={<IconUpload size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onUploadFile(contract);
            }}
          >
            Загрузить версию
          </Menu.Item>

          <Menu.Divider />

          <Menu.Item
            leftSection={<IconPencil size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(contract);
            }}
          >
            Редактировать
          </Menu.Item>

          {contract.is_active ? (
            <Menu.Item
              leftSection={<IconX size={14} />}
              color="orange"
              onClick={(e) => {
                e.stopPropagation();
                onDeactivate(contract);
              }}
            >
              Деактивировать
            </Menu.Item>
          ) : (
            <Menu.Item
              leftSection={<IconCheck size={14} />}
              color="green"
              onClick={(e) => {
                e.stopPropagation();
                onRestore(contract);
              }}
            >
              Восстановить
            </Menu.Item>
          )}

          <Menu.Divider />
          <Menu.Item
            leftSection={<IconTrash size={14} />}
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(contract);
            }}
          >
            Удалить
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

/**
 * Компонент отображения реквизитов
 */
const RequisitesItem = ({
  requisites,
  contracts,
  onEdit,
  onDelete,
  onView,
  onDeactivate,
  onRestore,
  onSetPrimary,
  onAddContract,
  // Колбэки для договоров
  onEditContract,
  onDeleteContract,
  onViewContract,
  onDeactivateContract,
  onRestoreContract,
  onDownloadContract,
  onUploadFileContract,
}) => {
  const isLegal = requisites.entity_type === 'legal';
  const isInactive = requisites.status === 'inactive';

  // Договоры для этих реквизитов
  const requisitesContracts = contracts.filter(c => c.requisites_id === requisites.id);

  return (
    <Stack gap="xs">
      <Group
        p="sm"
        style={{
          borderRadius: '12px',
          backgroundColor: isInactive ? tokens.colors.neutral[2] : tokens.colors.neutral[1],
          transition: 'background-color 150ms ease',
          cursor: 'pointer',
          opacity: isInactive ? 0.7 : 1,
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = tokens.colors.neutral[2]}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isInactive ? tokens.colors.neutral[2] : tokens.colors.neutral[1]}
        onClick={() => onView(requisites)}
      >
        <Box
          style={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: isLegal ? '#e3f2fd' : '#fff3e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isLegal ? (
            <IconBuildingBank size={20} color="#1976d2" />
          ) : (
            <IconUser size={20} color="#e65100" />
          )}
        </Box>

        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={500} truncate>
              {requisites.company_name}
            </Text>
            {requisites.is_primary && (
              <Badge
                size="xs"
                variant="filled"
                radius="xl"
                color="green"
                leftSection={<IconStarFilled size={10} />}
              >
                Основные
              </Badge>
            )}
            {isInactive && (
              <Badge
                size="xs"
                variant="filled"
                radius="xl"
                color="red"
                leftSection={<IconX size={10} />}
              >
                Не используются
              </Badge>
            )}
            {requisites.edit_count > 0 && !isInactive && (
              <Badge
                size="xs"
                variant="light"
                radius="xl"
                color="blue"
              >
                Изменено {requisites.edit_count}
              </Badge>
            )}
          </Group>

          <Text size="xs" c="dimmed">
            ИНН: {requisites.inn}
            {requisites.kpp && ` • КПП: ${requisites.kpp}`}
            {' • '}
            {isLegal ? `ОГРН: ${requisites.ogrn}` : `ОГРНИП: ${requisites.ogrn}`}
          </Text>

          {requisites.contract_start_date && (
            <Group gap={4} mt={4}>
              <IconCalendar size={12} color={tokens.colors.neutral[6]} />
              <Text size="xs" c="dimmed">
                Договор с {new Date(requisites.contract_start_date).toLocaleDateString('ru-RU')}
              </Text>
            </Group>
          )}
        </Box>

        {/* Меню действий */}
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
            <Menu.Item
              leftSection={<IconFileDescription size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onAddContract(requisites);
              }}
            >
              Добавить договор
            </Menu.Item>

            <Menu.Divider />

            <Menu.Item
              leftSection={<IconPencil size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(requisites);
              }}
            >
              Редактировать
            </Menu.Item>

            {!requisites.is_primary && requisites.status === 'active' && (
              <Menu.Item
                leftSection={<IconStarFilled size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetPrimary(requisites);
                }}
              >
                Сделать основными
              </Menu.Item>
            )}

            {requisites.status === 'active' ? (
              <Menu.Item
                leftSection={<IconX size={14} />}
                color="orange"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeactivate(requisites);
                }}
              >
                Не используются
              </Menu.Item>
            ) : (
              <Menu.Item
                leftSection={<IconCheck size={14} />}
                color="green"
                onClick={(e) => {
                  e.stopPropagation();
                  onRestore(requisites);
                }}
              >
                Восстановить
              </Menu.Item>
            )}

            <Menu.Divider />
            <Menu.Item
              leftSection={<IconTrash size={14} />}
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(requisites);
              }}
            >
              Удалить
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Договоры для этих реквизитов */}
      {requisitesContracts.map((contract) => (
        <ContractItem
          key={contract.id}
          contract={contract}
          onEdit={onEditContract}
          onDelete={onDeleteContract}
          onView={onViewContract}
          onDeactivate={onDeactivateContract}
          onRestore={onRestoreContract}
          onDownload={onDownloadContract}
          onUploadFile={onUploadFileContract}
        />
      ))}
    </Stack>
  );
};

/**
 * Основной компонент виджета
 */
const RequisitesWidget = ({ organization, onActivityChange }) => {
  const [requisitesList, setRequisitesList] = useState([]);
  const [contractsList, setContractsList] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Модальные окна для реквизитов
  const [requisitesModalOpen, setRequisitesModalOpen] = useState(false);
  const [editingRequisites, setEditingRequisites] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingRequisites, setViewingRequisites] = useState(null);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deactivatingRequisites, setDeactivatingRequisites] = useState(null);
  const [deactivateMode, setDeactivateMode] = useState('deactivate');

  // Модалка выбора способа добавления + ссылки
  const [addChoiceModalOpen, setAddChoiceModalOpen] = useState(false);
  const [requisitesLinks, setRequisitesLinks] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);

  // Модальные окна для договоров
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [selectedRequisites, setSelectedRequisites] = useState(null);
  const [contractViewModalOpen, setContractViewModalOpen] = useState(false);
  const [viewingContract, setViewingContract] = useState(null);
  const [contractDeactivateModalOpen, setContractDeactivateModalOpen] = useState(false);
  const [deactivatingContract, setDeactivatingContract] = useState(null);
  const [contractDeactivateMode, setContractDeactivateMode] = useState('deactivate');

  // Загрузка контактов
  const loadContacts = useCallback(async () => {
    if (!organization?.id) return;

    try {
      const response = await fetch(`/api/organizations/${organization.id}/contacts`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load contacts:', err);
    }
  }, [organization?.id]);

  // Загрузка реквизитов
  const loadRequisites = useCallback(async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      const data = await requisitesApi.getRequisites(organization.id);
      setRequisitesList(data.items || []);
    } catch (err) {
      console.error('Failed to load requisites:', err);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  // Загрузка договоров
  const loadContracts = useCallback(async () => {
    if (!organization?.id) return;

    try {
      const data = await contractsApi.getContracts(organization.id);
      setContractsList(data.items || []);
    } catch (err) {
      console.error('Failed to load contracts:', err);
    }
  }, [organization?.id]);

  // Загрузка ссылок на заполнение
  const loadLinks = useCallback(async () => {
    if (!organization?.id) return;

    try {
      const data = await requisitesApi.getLinks(organization.id);
      setRequisitesLinks(data.items || []);
    } catch (err) {
      console.error('Failed to load requisites links:', err);
    }
  }, [organization?.id]);

  // Загрузка предметов
  const loadSubjects = useCallback(async () => {
    try {
      const data = await subjectsApi.getSubjects();
      setSubjects(data.items || data || []);
    } catch (err) {
      console.error('Failed to load subjects:', err);
    }
  }, []);

  useEffect(() => {
    loadRequisites();
    loadContracts();
    loadContacts();
    loadSubjects();
    loadLinks();
  }, [loadRequisites, loadContracts, loadContacts, loadSubjects, loadLinks]);

  // ==================== Обработчики реквизитов ====================

  const handleAddRequisites = () => {
    setAddChoiceModalOpen(true);
  };

  // Выбор «Заполнить вручную» в модалке
  const handleChooseManual = () => {
    setAddChoiceModalOpen(false);
    setEditingRequisites(null);
    setRequisitesModalOpen(true);
  };

  // Выбор «Сгенерировать ссылку» в модалке
  const handleChooseLink = async () => {
    setAddChoiceModalOpen(false);
    setLinkLoading(true);
    try {
      await requisitesApi.createLink(organization.id);
      await loadLinks();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to create link:', err);
      alert(err.message);
    } finally {
      setLinkLoading(false);
    }
  };

  // Перегенерация ссылки
  const handleRegenerateLink = async (linkId) => {
    setLinkLoading(true);
    try {
      await requisitesApi.regenerateLink(organization.id, linkId);
      await loadLinks();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to regenerate link:', err);
      alert(err.message);
    } finally {
      setLinkLoading(false);
    }
  };

  const handleEditRequisites = (requisites) => {
    setEditingRequisites(requisites);
    setRequisitesModalOpen(true);
  };

  const handleViewRequisites = (requisites) => {
    setViewingRequisites(requisites);
    setViewModalOpen(true);
  };

  const handleDeleteRequisites = async (requisites) => {
    if (!window.confirm(`Удалить реквизиты "${requisites.company_name}"?`)) {
      return;
    }

    try {
      await requisitesApi.deleteRequisites(organization.id, requisites.id);
      await loadRequisites();
      await loadContracts();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to delete requisites:', err);
      alert(err.message);
    }
  };

  const handleDeactivateRequisites = (requisites) => {
    setDeactivatingRequisites(requisites);
    setDeactivateMode('deactivate');
    setDeactivateModalOpen(true);
  };

  const handleRestoreRequisites = (requisites) => {
    setDeactivatingRequisites(requisites);
    setDeactivateMode('restore');
    setDeactivateModalOpen(true);
  };

  const handleDeactivateConfirm = async (reason) => {
    setActionLoading(true);
    try {
      if (deactivateMode === 'deactivate') {
        await requisitesApi.deactivateRequisites(organization.id, deactivatingRequisites.id, reason);
      } else {
        await requisitesApi.restoreRequisites(organization.id, deactivatingRequisites.id, reason);
      }
      setDeactivateModalOpen(false);
      setDeactivatingRequisites(null);
      await loadRequisites();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPrimary = async (requisites) => {
    try {
      await requisitesApi.setPrimary(organization.id, requisites.id);
      await loadRequisites();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to set primary:', err);
      alert(err.message);
    }
  };

  const handleSubmitRequisites = async (data) => {
    setActionLoading(true);
    try {
      if (editingRequisites) {
        await requisitesApi.updateRequisites(organization.id, editingRequisites.id, data);
      } else {
        await requisitesApi.createRequisites(organization.id, data);
      }
      setRequisitesModalOpen(false);
      setEditingRequisites(null);
      await loadRequisites();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // ==================== Обработчики договоров ====================

  const handleAddContract = (requisites) => {
    setSelectedRequisites(requisites);
    setEditingContract(null);
    setContractModalOpen(true);
  };

  const handleEditContract = (contract) => {
    // Найти реквизиты для этого договора
    const requisites = requisitesList.find(r => r.id === contract.requisites_id);
    setSelectedRequisites(requisites);
    setEditingContract(contract);
    setContractModalOpen(true);
  };

  const handleViewContract = (contract) => {
    setViewingContract(contract);
    setContractViewModalOpen(true);
  };

  const handleDeleteContract = async (contract) => {
    if (!window.confirm(`Удалить договор "${contract.display_name}"?`)) {
      return;
    }

    try {
      await contractsApi.deleteContract(organization.id, contract.id);
      await loadContracts();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to delete contract:', err);
      alert(err.message);
    }
  };

  const handleDeactivateContract = (contract) => {
    setDeactivatingContract(contract);
    setContractDeactivateMode('deactivate');
    setContractDeactivateModalOpen(true);
  };

  const handleRestoreContract = (contract) => {
    setDeactivatingContract(contract);
    setContractDeactivateMode('restore');
    setContractDeactivateModalOpen(true);
  };

  const handleContractDeactivateConfirm = async (reason) => {
    setActionLoading(true);
    try {
      if (contractDeactivateMode === 'deactivate') {
        await contractsApi.deactivateContract(organization.id, deactivatingContract.id, reason);
      } else {
        await contractsApi.restoreContract(organization.id, deactivatingContract.id, reason);
      }
      setContractDeactivateModalOpen(false);
      setDeactivatingContract(null);
      await loadContracts();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadContract = (contract) => {
    window.open(
      `/api/organizations/${organization.id}/contracts/${contract.id}/download`,
      '_blank'
    );
  };

  const handleUploadFileContract = (contract) => {
    // Открыть диалог выбора файла
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.txt,.doc,.docx,.jpg,.jpeg,.png';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        setActionLoading(true);
        await contractsApi.uploadFile(organization.id, contract.id, file);
        await loadContracts();
        if (onActivityChange) {
          onActivityChange();
        }
      } catch (err) {
        console.error('Failed to upload file:', err);
        alert(err.message);
      } finally {
        setActionLoading(false);
      }
    };
    input.click();
  };

  const handleSubmitContract = async (data, file) => {
    setActionLoading(true);
    try {
      if (editingContract) {
        await contractsApi.updateContract(organization.id, editingContract.id, data);
        // Если есть новый файл, загрузить его
        if (file) {
          await contractsApi.uploadFile(organization.id, editingContract.id, file);
        }
      } else {
        await contractsApi.createContract(organization.id, selectedRequisites.id, data, file);
      }
      setContractModalOpen(false);
      setEditingContract(null);
      setSelectedRequisites(null);
      await loadContracts();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // Подсчёт общего количества (реквизиты + договоры)
  const totalCount = requisitesList.length + contractsList.length;

  return (
    <>
      <BaseWidget
        title="Реквизиты и Договоры"
        icon={IconFileText}
        count={totalCount}
        collapsible={true}
        defaultExpanded={totalCount > 0}
        actions={
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              handleAddRequisites();
            }}
          >
            Добавить
          </Button>
        }
      >
        {/* Секция ссылок на заполнение */}
        <RequisitesLinkSection
          links={requisitesLinks}
          loading={linkLoading}
          onRegenerate={handleRegenerateLink}
        />

        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : requisitesList.length > 0 ? (
          <Stack gap="md">
            {requisitesList.map((requisites) => (
              <RequisitesItem
                key={requisites.id}
                requisites={requisites}
                contracts={contractsList}
                onEdit={handleEditRequisites}
                onDelete={handleDeleteRequisites}
                onView={handleViewRequisites}
                onDeactivate={handleDeactivateRequisites}
                onRestore={handleRestoreRequisites}
                onSetPrimary={handleSetPrimary}
                onAddContract={handleAddContract}
                // Колбэки для договоров
                onEditContract={handleEditContract}
                onDeleteContract={handleDeleteContract}
                onViewContract={handleViewContract}
                onDeactivateContract={handleDeactivateContract}
                onRestoreContract={handleRestoreContract}
                onDownloadContract={handleDownloadContract}
                onUploadFileContract={handleUploadFileContract}
              />
            ))}
          </Stack>
        ) : (
          <Box py="lg" style={{ textAlign: 'center' }}>
            <IconFileText
              size={40}
              style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
            />
            <Text size="sm" c="dimmed">
              Реквизиты не добавлены
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              Добавьте реквизиты организации
            </Text>
            <Button
              variant="light"
              size="xs"
              mt="md"
              leftSection={<IconPlus size={14} />}
              onClick={handleAddRequisites}
            >
              Добавить реквизиты
            </Button>
          </Box>
        )}
      </BaseWidget>

      {/* Модальные окна для реквизитов */}
      <RequisitesFormModal
        opened={requisitesModalOpen}
        onClose={() => {
          setRequisitesModalOpen(false);
          setEditingRequisites(null);
        }}
        onSubmit={handleSubmitRequisites}
        requisites={editingRequisites}
        organizationId={organization?.id}
        organization={organization}
        contacts={contacts || []}
        loading={actionLoading}
      />

      <RequisitesViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingRequisites(null);
        }}
        requisites={viewingRequisites}
        onEdit={handleEditRequisites}
        onAddContract={handleAddContract}
      />

      <RequisitesDeactivateModal
        opened={deactivateModalOpen}
        onClose={() => {
          setDeactivateModalOpen(false);
          setDeactivatingRequisites(null);
        }}
        onConfirm={handleDeactivateConfirm}
        requisites={deactivatingRequisites}
        mode={deactivateMode}
        loading={actionLoading}
      />

      {/* Модальные окна для договоров */}
      <ContractFormModal
        opened={contractModalOpen}
        onClose={() => {
          setContractModalOpen(false);
          setEditingContract(null);
          setSelectedRequisites(null);
        }}
        onSubmit={handleSubmitContract}
        contract={editingContract}
        requisites={selectedRequisites}
        organizationId={organization?.id}
        subjects={subjects}
        loading={actionLoading}
      />

      <ContractViewModal
        opened={contractViewModalOpen}
        onClose={() => {
          setContractViewModalOpen(false);
          setViewingContract(null);
        }}
        contract={viewingContract}
        organizationId={organization?.id}
        onEdit={handleEditContract}
        onUploadFile={handleUploadFileContract}
      />

      <ContractDeactivateModal
        opened={contractDeactivateModalOpen}
        onClose={() => {
          setContractDeactivateModalOpen(false);
          setDeactivatingContract(null);
        }}
        onConfirm={handleContractDeactivateConfirm}
        contract={deactivatingContract}
        mode={contractDeactivateMode}
        loading={actionLoading}
      />

      {/* Модалка выбора способа добавления реквизитов */}
      <RequisitesAddChoiceModal
        opened={addChoiceModalOpen}
        onClose={() => setAddChoiceModalOpen(false)}
        onChooseLink={handleChooseLink}
        onChooseManual={handleChooseManual}
      />
    </>
  );
};

export default RequisitesWidget;
